// Version: 1.11.0
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { CartProvider } from './contexts/CartContext';
import Header from './components/Header';
import Banner from './components/Banner';
import { useVisitorTracking } from './hooks/useVisitorTracking';
import DetailPage from './pages/DetailPage';
import AdminPage from './pages/AdminPage';
import AddDesignPage from './pages/AddDesignPage';
import SettingsPage from './pages/SettingsPage';
import FormatsAdminPage from './pages/FormatsAdminPage';
import MockupEditorPage from './pages/MockupEditorPage';
import LoginPage from './pages/LoginPage';
import CheckoutPage from './pages/CheckoutPage';
import CheckoutSuccessPage from './pages/CheckoutSuccessPage';
import OrdersPage from './pages/OrdersPage';
import OrderDetailsPage from './pages/OrderDetailsPage';
import VisitorActivityPage from './pages/VisitorActivityPage';
import SessionDetailPage from './pages/SessionDetailPage';
import MaintenancePage from './pages/MaintenancePage';
import RedirectsAdminPage from './pages/RedirectsAdminPage';
import CategoriesAndTagsPage from './pages/CategoriesAndTagsPage';
import NoteDisplay from './pages/NoteDisplay';
import NotesListPage from './pages/NotesListPage';
import ExternalImagesPage from './pages/ExternalImagesPage';
import ThankYouPage from './pages/ThankYouPage';
import ContactInquiriesPage from './pages/ContactInquiriesPage';
import MailingListSubscribersPage from './pages/MailingListSubscribersPage';
import MarketingPage from './pages/MarketingPage';
import VisitorAnalysisPage from './pages/VisitorAnalysisPage';
import OutreachPage from './pages/OutreachPage';
import GalleryPage from './pages/GalleryPage';
import ShowcasePage from './pages/ShowcasePage';
import BundlesPage from './pages/BundlesPage';
import BundlePage from './pages/BundlePage';
import AdminBundlesPage from './pages/AdminBundlesPage';
import ProtectedRoute from './components/ProtectedRoute';
import RedirectChecker from './components/RedirectChecker';
import DefaultPageRedirect from './components/DefaultPageRedirect';
import GlobalModalManager from './components/GlobalModalManager';
import { API_BASE_URL } from './config/api';

// Component to track visitor activity
function VisitorTracker() {
  useVisitorTracking();
  return null; // This component doesn't render anything
}

// Component to check maintenance mode
function MaintenanceCheck() {
  const [isMaintenance, setIsMaintenance] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const checkMaintenance = async () => {
      try {
        // Check if user is logged in as admin
        const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
        let userIsAdmin = false;
        
        if (token) {
          try {
            const verifyResponse = await axios.get(`${API_BASE_URL}/auth/verify`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            userIsAdmin = verifyResponse.data.success === true;
          } catch (err) {
            // Token invalid or expired - silently handle (expected for non-admin users)
            userIsAdmin = false;
            // Clear invalid token
            if (err.response?.status === 401 || err.response?.status === 403) {
              localStorage.removeItem('authToken');
              sessionStorage.removeItem('authToken');
            }
          }
        }
        
        setIsAdmin(userIsAdmin);
        
        // Allow admin routes to bypass maintenance mode
        const isAdminRoute = location.pathname.startsWith('/admin') || location.pathname === '/login';
        
        if (isAdminRoute) {
          setIsMaintenance(false);
          setLoading(false);
          return;
        }

        // Check maintenance mode
        const response = await axios.get(`${API_BASE_URL}/config`);
        if (response.data.success && response.data.data?.maintenance?.enabled) {
          // If maintenance is enabled, only block if user is not admin
          // Allow gallery, design pages, and checkout for admin users
          if (userIsAdmin && (
            location.pathname === '/' || 
            location.pathname.startsWith('/design/') ||
            location.pathname === '/checkout' ||
            location.pathname.startsWith('/checkout/')
          )) {
            setIsMaintenance(false);
          } else {
            setIsMaintenance(true);
          }
        } else {
          setIsMaintenance(false);
        }
      } catch (error) {
        // If we can't check maintenance mode, allow access
        console.error('Error checking maintenance mode:', error);
        setIsMaintenance(false);
      } finally {
        setLoading(false);
      }
    };

    checkMaintenance();
  }, [location.pathname]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (isMaintenance) {
    return <MaintenancePage />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <GlobalModalManager />
      <VisitorTracker />
      <Banner />
      <Header isAdmin={isAdmin} />
      <Routes>
        <Route path="/" element={<DefaultPageRedirect />} />
        <Route path="/t" element={<Navigate to="/t/all" replace />} />
        <Route path="/t/:tags" element={<GalleryPage />} />
        <Route path="/showcase/:format" element={<ShowcasePage />} />
        <Route path="/showcase/:format/:category" element={<ShowcasePage />} />
        <Route path="/bundles" element={<BundlesPage />} />
        <Route path="/bundle/:id" element={<BundlePage />} />
        <Route path="/design/:id" element={<DetailPage />} />
        <Route path="/search" element={<CategoriesAndTagsPage />} />
        <Route path="/notes" element={<NotesListPage />} />
        <Route path="/note/:noteName" element={<NoteDisplay />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
        <Route path="/mailing-list/thank-you" element={<ThankYouPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route 
          path="/admin" 
          element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/gallery" 
          element={
            <ProtectedRoute>
              <AdminPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/marketing" 
          element={
            <ProtectedRoute>
              <MarketingPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/add-design" 
          element={
            <ProtectedRoute>
              <AddDesignPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/bundles" 
          element={
            <ProtectedRoute>
              <AdminBundlesPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/settings" 
          element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/formats" 
          element={
            <ProtectedRoute>
              <FormatsAdminPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/formats/:formatId/mockups/:mockupIndex" 
          element={
            <ProtectedRoute>
              <MockupEditorPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/orders" 
          element={
            <ProtectedRoute>
              <OrdersPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/orders/:orderId" 
          element={
            <ProtectedRoute>
              <OrderDetailsPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/visitor-activity" 
          element={
            <ProtectedRoute>
              <VisitorActivityPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/visitor-activity/:date" 
          element={
            <ProtectedRoute>
              <SessionDetailPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/visitor-analysis" 
          element={
            <ProtectedRoute>
              <VisitorAnalysisPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/redirects" 
          element={
            <ProtectedRoute>
              <RedirectsAdminPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/external-images" 
          element={
            <ProtectedRoute>
              <ExternalImagesPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/contact-inquiries" 
          element={
            <ProtectedRoute>
              <ContactInquiriesPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/mailing-list" 
          element={
            <ProtectedRoute>
              <MailingListSubscribersPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/outreach" 
          element={
            <ProtectedRoute>
              <OutreachPage />
            </ProtectedRoute>
          } 
        />
        {/* Dual routing for notes: /notes/:slug and /:slug both work */}
        {/* Note: /:slug must come after all specific routes to avoid conflicts */}
        <Route path="/notes/:slug" element={<NoteDisplay />} />
        <Route path="/:slug" element={<NoteDisplay />} />
        {/* Catch-all route - check for redirects before showing 404 */}
        <Route 
          path="*" 
          element={
            <RedirectChecker>
              <Navigate to="/note/404" replace />
            </RedirectChecker>
          } 
        />
      </Routes>
    </div>
  );
}

function App() {
  return (
    <CartProvider>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <MaintenanceCheck />
      </Router>
    </CartProvider>
  );
}

export default App;

